/**
 * Case Conversion Utilities for Schema GraphOS
 * Converts strings between different naming conventions
 */

/**
 * Convert string to snake_case
 */
export const toSnakeCase = (str) => {
  if (!str) return str;
  
  return str
    // Insert underscore before uppercase letters
    .replace(/([A-Z])/g, '_$1')
    // Replace spaces and hyphens with underscores
    .replace(/[\s-]+/g, '_')
    // Remove leading underscore
    .replace(/^_/, '')
    // Convert to lowercase
    .toLowerCase()
    // Remove multiple consecutive underscores
    .replace(/_+/g, '_');
};

/**
 * Convert string to camelCase
 */
export const toCamelCase = (str) => {
  if (!str) return str;
  
  return str
    // Split by spaces, hyphens, underscores
    .split(/[\s_-]+/)
    // Capitalize first letter of each word except the first
    .map((word, index) => {
      if (index === 0) {
        return word.toLowerCase();
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join('');
};

/**
 * Convert string to PascalCase
 */
export const toPascalCase = (str) => {
  if (!str) return str;
  
  return str
    // Split by spaces, hyphens, underscores
    .split(/[\s_-]+/)
    // Capitalize first letter of each word
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join('');
};

/**
 * Convert string to kebab-case
 */
export const toKebabCase = (str) => {
  if (!str) return str;
  
  return str
    // Insert hyphen before uppercase letters
    .replace(/([A-Z])/g, '-$1')
    // Replace spaces and underscores with hyphens
    .replace(/[\s_]+/g, '-')
    // Remove leading hyphen
    .replace(/^-/, '')
    // Convert to lowercase
    .toLowerCase()
    // Remove multiple consecutive hyphens
    .replace(/-+/g, '-');
};

/**
 * Convert string to UPPERCASE
 */
export const toUpperCase = (str) => {
  if (!str) return str;
  return str.toUpperCase();
};

/**
 * Convert string to lowercase
 */
export const toLowerCase = (str) => {
  if (!str) return str;
  return str.toLowerCase();
};

/**
 * Apply case conversion to a string
 */
export const applyCase = (str, caseType) => {
  switch (caseType) {
    case 'snake':
      return toSnakeCase(str);
    case 'camel':
      return toCamelCase(str);
    case 'pascal':
      return toPascalCase(str);
    case 'kebab':
      return toKebabCase(str);
    case 'upper':
      return toUpperCase(str);
    case 'lower':
      return toLowerCase(str);
    default:
      return str;
  }
};

/**
 * Get example of case conversion
 */
export const getCaseExample = (caseType) => {
  const examples = {
    snake: 'user_profile_data',
    camel: 'userProfileData',
    pascal: 'UserProfileData',
    kebab: 'user-profile-data',
    upper: 'USER PROFILE DATA',
    lower: 'user profile data'
  };
  return examples[caseType] || 'example';
};

/**
 * Detect current case of a string
 */
export const detectCase = (str) => {
  if (!str) return 'unknown';
  
  if (str === str.toUpperCase() && str.indexOf(' ') === -1) return 'upper';
  if (str === str.toLowerCase() && str.indexOf(' ') === -1 && str.indexOf('_') === -1 && str.indexOf('-') === -1) return 'lower';
  if (str.indexOf('_') !== -1) return 'snake';
  if (str.indexOf('-') !== -1) return 'kebab';
  if (/^[A-Z]/.test(str) && /[a-z]/.test(str) && str.indexOf(' ') === -1) {
    // Check if it's PascalCase or camelCase
    if (/^[A-Z]/.test(str)) return 'pascal';
  }
  if (/^[a-z]/.test(str) && /[A-Z]/.test(str) && str.indexOf(' ') === -1) return 'camel';
  
  return 'mixed';
};
